/**
 * Field Validators
 * Individual field validation functions
 */

import { PhoneUtils } from '../utils/phone_utils.js';
import { DateUtils } from '../utils/date_utils.js';
import { FormatUtils } from '../utils/format_utils.js';

export class FieldValidators {
  /**
   * Validate required field
   * @param {Element} field - Field element
   * @param {Translator} translator - Translator instance
   * @returns {Object}
   */
  static validateRequired(field, translator = null) {
    const isEmpty = !field.value || field.value.trim() === '';
    const t = translator?.t.bind(translator) || ((key) => key);
    
    if (field.type === 'checkbox' || field.type === 'radio') {
      const fieldName = field.name || field.id;
      // Use getElementsByName so names with "[]" (e.g. field_123[]) work reliably
      const inputs = document.getElementsByName(fieldName);
      const hasChecked = Array.from(inputs).some((el) => el.checked);
      return {
        isValid: hasChecked,
        errorMessage: hasChecked ? '' : t('errors.please_select_option')
      };
    }

    // In edit mode, existing files are shown as "Open File" links; file input has no value
    if (field.type === 'file') {
      const fieldId = field.name || field.id;
      if (fieldId) {
        const scope = field.closest('form') || document;
        const fileRefs = scope.querySelectorAll(`.file_refernce[data-field_id="${fieldId}"]`);
        if (fileRefs.length > 0) {
          return { isValid: true, errorMessage: '' };
        }
      }
    }

    return {
      isValid: !isEmpty,
      errorMessage: isEmpty ? t('errors.entry_too_short') : ''
    };
  }

  /**
   * Validate email
   * @param {Element} field - Field element
   * @param {Translator} translator - Translator instance
   * @returns {Object}
   */
  static validateEmail(field, translator = null) {
    if (!field.value || !field.value.trim()) {
      return { isValid: true, errorMessage: '' }; // Empty is handled by required validator
    }
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const isValid = emailRegex.test(field.value);
    const t = translator?.t.bind(translator) || ((key) => key);
    
    return {
      isValid,
      errorMessage: isValid ? '' : t('errors.enter_valid_email')
    };
  }

  /**
   * Validate phone number
   * @param {Element} field - Field element
   * @param {string} countryCode - Country code
   * @param {Translator} translator - Translator instance
   * @returns {Object}
   */
  static validatePhone(field, countryCode = '', translator = null) {
    if (!field.value || !field.value.trim()) {
      return { isValid: true, errorMessage: '' }; // Empty is handled by required validator
    }
    
    const t = translator?.t.bind(translator) || ((key) => key);
    
    // If using intl-tel-input, check if instance exists
    if (field.itiInstance) {
      const isValid = field.itiInstance.isValidNumber();
      return {
        isValid,
        errorMessage: isValid ? '' : t('errors.enter_valid_phone')
      };
    }
    
    // Fallback validation
    return {
      isValid: field.value.length >= 10,
      errorMessage: field.value.length >= 10 ? '' : t('errors.enter_valid_phone')
    };
  }

  /**
   * Validate US NPI
   * @param {Element} field - Field element
   * @returns {Object}
   */
  static validateNPI(field, translator = null) {
    if (!field.value || !field.value.trim()) {
      return { isValid: true, errorMessage: '' }; // Empty is handled by required validator
    }
    
    const isValid = PhoneUtils.validateUSNPI(field.value);
    const t = translator?.t.bind(translator) || ((key) => key);
    
    return {
      isValid,
      errorMessage: isValid ? '' : t('errors.enter_10_digit_npi')
    };
  }

  /**
   * Validate Israeli ID
   * @param {Element} field - Field element
   * @returns {Object}
   */
  static validateIsraeliID(field, translator = null) {
    if (!field.value || !field.value.trim()) {
      return { isValid: true, errorMessage: '' }; // Empty is handled by required validator
    }
    
    const isValid = PhoneUtils.validateIsraeliID(field.value);
    const t = translator?.t.bind(translator) || ((key) => key);
    
    return {
      isValid,
      errorMessage: isValid ? '' : t('errors.enter_valid_id')
    };
  }

  /**
   * Validate Israeli phone
   * @param {Element} field - Field element
   * @returns {Object}
   */
  static validateIsraeliPhone(field, translator = null) {
    if (!field.value || !field.value.trim()) {
      return { isValid: true, errorMessage: '' }; // Empty is handled by required validator
    }
    
    const isValid = PhoneUtils.validateIsraeliPhone(field.value);
    const t = translator?.t.bind(translator) || ((key) => key);
    
    return {
      isValid,
      errorMessage: isValid ? '' : t('errors.enter_valid_phone')
    };
  }

  /**
   * Validate letters only (any language - Unicode letters \p{L})
   * @param {Element} field - Field element
   * @param {Translator} translator - Translator instance
   * @returns {Object}
   */
  static validateLettersOnly(field, translator = null) {
    if (!field.value || !field.value.trim()) {
      return { isValid: true, errorMessage: '' }; // Empty is handled by required validator
    }
    const isValid = /^\p{L}+$/u.test(field.value.trim());
    const t = translator?.t.bind(translator) || ((key) => key);
    return {
      isValid,
      errorMessage: isValid ? '' : t('errors.letters_only')
    };
  }

  /**
   * Validate date
   * @param {Element} field - Field element
   * @param {Translator} translator - Translator instance
   * @param {Object} fieldConfig - Field configuration
   * @returns {Object}
   */
  static validateDate(field, translator = null, fieldConfig = {}) {
    if (!field.value || !field.value.trim()) {
      return { isValid: true, errorMessage: '' }; // Empty is handled by required validator
    }
    
    const t = translator?.t.bind(translator) || ((key) => key);
    
    // Check if date is valid
    if (!DateUtils.isValidDate(field.value)) {
      return {
        isValid: false,
        errorMessage: t('errors.enter_valid_date')
      };
    }
    
    // Check date limitation based on field configuration
    const dateLimitation = fieldConfig.dateLimitation;
    
    if (dateLimitation === 'future') {
      // Reject past dates
      if (DateUtils.isPastDate(field.value)) {
        return {
          isValid: false,
          errorMessage: t('errors.date_cannot_be_past')
        };
      }
    } else if (dateLimitation === 'past') {
      // Reject future dates
      if (DateUtils.isFutureDate(field.value)) {
        return {
          isValid: false,
          errorMessage: t('errors.date_cannot_be_future')
        };
      }
    } else if (dateLimitation === 'between') {
      // Validate date range
      if (fieldConfig.dateFromLimit && fieldConfig.dateToLimit) {
        if (!DateUtils.isDateInRange(field.value, fieldConfig.dateFromLimit, fieldConfig.dateToLimit)) {
          return {
            isValid: false,
            errorMessage: t('errors.date_out_of_range')
          };
        }
      }
    } else {
      // Default: reject future dates (backward compatibility)
      if (DateUtils.isFutureDate(field.value)) {
        return {
          isValid: false,
          errorMessage: t('errors.date_cannot_be_future')
        };
      }
    }
    
    return {
      isValid: true,
      errorMessage: ''
    };
  }

  /**
   * Validate number
   * @param {Element} field - Field element
   * @returns {Object}
   */
  static validateNumber(field, translator = null) {
    if (!field.value || !field.value.trim()) {
      return { isValid: true, errorMessage: '' }; // Empty is handled by required validator
    }
    
    const value = parseFloat(field.value);
    const t = translator?.t.bind(translator) || ((key) => key);
    
    if (isNaN(value)) {
      return {
        isValid: false,
        errorMessage: t('errors.enter_valid_number')
      };
    }
    
    const min = field.hasAttribute('min') ? parseFloat(field.getAttribute('min')) : null;
    const max = field.hasAttribute('max') ? parseFloat(field.getAttribute('max')) : null;
    const step = field.hasAttribute('step') ? parseFloat(field.getAttribute('step')) : 1;
    
    if (min !== null && value < min) {
      return {
        isValid: false,
        errorMessage: t('errors.value_must_be_at_least', { min })
      };
    }
    
    if (max !== null && value > max) {
      return {
        isValid: false,
        errorMessage: t('errors.value_must_be_at_most', { max })
      };
    }
    
    // Check step
    if (min !== null) {
      const remainder = (value - min) % step;
      if (Math.abs(remainder) > 0.0001) { // Floating point tolerance
        return {
          isValid: false,
          errorMessage: t('errors.value_must_be_in_steps', { step })
        };
      }
    }
    
    return {
      isValid: true,
      errorMessage: ''
    };
  }

  /**
   * Validate max characters
   * @param {Element} field - Field element
   * @param {number} maxChars - Maximum characters
   * @returns {Object}
   */
  static validateMaxChars(field, maxChars, translator = null) {
    if (!field.value) {
      return { isValid: true, errorMessage: '' };
    }
    
    const isValid = field.value.length <= maxChars;
    const t = translator?.t.bind(translator) || ((key) => key);
    
    return {
      isValid,
      errorMessage: isValid ? '' : t('errors.maximum_characters_allowed', { maxChars })
    };
  }

  /**
   * Validate custom input function
   * @param {Element} field - Field element
   * @param {string} inputFunction - Input function name
   * @returns {Object}
   */
  static validateInputFunction(field, inputFunction, translator = null) {
    if (!field.value || !field.value.trim()) {
      return { isValid: true, errorMessage: '' }; // Empty is handled by required validator
    }
    
    switch (inputFunction) {
      case 'usNPI':
        return this.validateNPI(field, translator);
      case 'israeliIDNumber':
        return this.validateIsraeliID(field, translator);
      case 'israeliPhoneNumber':
        return this.validateIsraeliPhone(field, translator);
      case 'lettersOnly':
        return this.validateLettersOnly(field, translator);
      default:
        return { isValid: true, errorMessage: '' };
    }
  }
}

